import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import MUIDataTable from 'mui-datatables'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { titleCase } from 'title-case'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import { Container } from 'src/modules/ui'
import { formatDateSearch } from 'src/utils'

import { useMessages } from '../hooks'

const useStyles = makeStyles(theme => ({
  root: {
    '& .unread td': {
      fontWeight: 'bold',
    },
  },
}))

export const Conversations = ({ conversations, title }) => {
  const history = useHistory()
  const { userType } = useAuth()
  const classes = useStyles()

  const data = conversations.map(
    ({ lastMessageAt, environment, buyer, supplier, ...conversation }) => ({
      ...conversation,
      lastMessageAt: formatDateSearch(lastMessageAt),
      environment: environment && environment.name,
      buyer: buyer && buyer.name,
      supplier: supplier && supplier.name,
    })
  )

  const otherPartyCol = {
    buyer: {
      name: 'supplier',
      label: 'Supplier',
    },
    supplier: {
      name: 'buyer',
      label: 'Customer',
    },
  }

  const columns = [
    {
      name: 'id',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      ...otherPartyCol[userType],
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'subject',
      label: 'Subject',
      options: {
        filter: false,
        sort: true,
      },
    },
  ]

  if (userType === 'buyer') {
    columns.push({
      name: 'environment',
      label: 'Environment',
      options: {
        filter: true,
        sort: true,
      },
    })
  }

  columns.push({
    name: 'lastMessageAt',
    label: 'Date',
    options: {
      filter: false,
      sort: true,
    },
  })

  const options = {
    download: false,
    elevation: 0,
    onRowClick: row => {
      history.push(`/messages/${row[0]}`)
    },
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'none',
    setRowProps: (row, dataIndex, rowIndex) => {
      const { unreadMessageCount } = data[dataIndex]
      return {
        className: clsx({
          unread: unreadMessageCount > 0,
        }),
      }
    },
    viewColumns: false,
  }

  return (
    <Container muiTable className={classes.root}>
      <MUIDataTable
        columns={columns}
        data={data}
        options={options}
        title={title}
      />
    </Container>
  )
}

Conversations.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      buyer: PropTypes.object,
      createdAt: PropTypes.string,
      environment: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      lastMessageAt: PropTypes.string,
      subject: PropTypes.string,
      supplier: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      unreadMessageCount: PropTypes.number,
    })
  ),
  title: PropTypes.string,
}

const ConversationsContainer = ({ section, title }) => {
  const { conversations, fetchConversations } = useMessages()
  const [doFetchConversations, isFetching] = useActionRunner(
    fetchConversations,
    {
      initialFetchingState: true,
    }
  )

  useEffect(() => {
    doFetchConversations(section)
  }, [])

  if (isFetching) {
    return null
  }

  return (
    <Conversations conversations={conversations} title={titleCase(section)} />
  )
}

export default ConversationsContainer
